<script setup lang="ts">
import { computed, markRaw, watchEffect } from 'vue';
import ParentIcon from '@/assets/icons/counterparty/master/parent.svg?component';
import type ClientCard from '@/modules/counterparty/models/card/client-card.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import { useAbort } from '@/modules/common/composables/use-abort.ts';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { CounterpartyApi } from '@/modules/counterparty/api/counterparty.api.ts';
import { useCounterpartyMaster } from '@/modules/counterparty/counterparty-master/counterparty-master.ts';
import { useWizardStep } from '@/providers/wizard.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import UiRadio, { type RadioOption } from '@/modules/ui/components/ui/UiRadio.vue';
import UiSearch from '@/modules/ui/components/ui/UiSearch.vue';
import UiCardList from '@/modules/ui/components/ui/UiCardList.vue';

const MODE = {
  none: 'none',
  subordinated: 'subordinated',
} as const;

type ModeCode = (typeof MODE)[keyof typeof MODE];

const OPTIONS: RadioOption<ModeCode>[] = [
  { code: MODE.none, label: 'counterparty.master.steps.subordination.none', color: 'gray' },
  { code: MODE.subordinated, label: 'counterparty.master.steps.subordination.subordinated', color: 'green' },
];

const { isValid } = useWizardStep();
const { abort, signal } = useAbort();
const { formData } = useCounterpartyMaster();

watchEffect(() => {
  isValid.value = !formData.value.isSubordinated || formData.value.parents.length > 0;
});

function isSameParentId(left: string | number | undefined, right: string | number | undefined) {
  return Number(left) === Number(right);
}

const radioModel = computed({
  get: (): ModeCode => (formData.value.isSubordinated ? MODE.subordinated : MODE.none),
  set: (value: ModeCode) => {
    const isSubordinated = value === MODE.subordinated;

    formData.value = {
      ...formData.value,
      isSubordinated,
      parents: isSubordinated ? formData.value.parents : [],
    };
  },
});

const parentOption = computed({
  get: (): Option | null => {
    const parent = formData.value.parents[0];

    if (!parent) {
      return null;
    }

    return {
      code: Number(parent.id),
      label: String(parent.title),
    };
  },
  set: (option: Option | null) => {
    void onParentOptionChange(option);
  },
});

function createPlaceholderParent(option: Option): ClientCard {
  return {
    id: option.code,
    title: option.label,
    subtitle: '',
    icon: markRaw(ParentIcon),
    href: undefined,
  } as unknown as ClientCard;
}

async function onParentOptionChange(option: Option | null) {
  if (!option) {
    if (formData.value.parents.length) {
      formData.value = {
        ...formData.value,
        parents: [],
      };
    }
    return;
  }

  if (isSameParentId(formData.value.parents[0]?.id, option.code)) {
    return;
  }

  abort();

  formData.value = {
    ...formData.value,
    parents: [createPlaceholderParent(option)],
  };

  try {
    const card = await CounterpartyApi.getClient(Number(option.code), signal.value);

    if (!isSameParentId(formData.value.parents[0]?.id, option.code)) {
      return;
    }

    const parent = formData.value.parents[0];

    Object.assign(parent, card, {
      icon: markRaw(ParentIcon),
      href: undefined,
      title: option.label,
      subtitle: '',
    });
  } catch (error) {
    if (!ErrorUtil.isUnexpected(error)) {
      return;
    }

    if (isSameParentId(formData.value.parents[0]?.id, option.code)) {
      formData.value = {
        ...formData.value,
        parents: [],
      };
    }

    notification.error(
      {
        key: 'ui.notification.error.field.getOption',
        params: { field: 'counterparty.master.steps.subordination.selectParent' },
      },
      error
    );
  }
}

function removeParent(id: string | number) {
  if (!isSameParentId(formData.value.parents[0]?.id, id)) {
    return;
  }

  formData.value = {
    ...formData.value,
    parents: [],
  };
}
</script>

<template>
  <UiRadio
    v-model="radioModel"
    class="step-subordination"
    :options="OPTIONS"
  >
    <template #1>
      <div class="parent-field">
        <UiSearch
          v-if="!formData.parents.length"
          v-model="parentOption"
          class="parent-search"
          placeholder="counterparty.master.steps.subordination.selectParent"
          :get-options="CounterpartyApi.getOptions"
        />

        <UiCardList
          v-else
          class="parent-card"
          :list="formData.parents"
          icon-spaced
          close-on-hover
          tooltip-on-card
          @remove="removeParent"
        />
      </div>
    </template>
  </UiRadio>
</template>

<style scoped lang="scss">
.step-subordination {
  display: flex;
  height: 100%;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
}

.parent-search {
  width: 100%;
}

.parent-field {
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
}

.parent-card {
  padding: 0;
  align-content: start;
}
</style>
